import {
	isSSEMessage,
	type ParsedSSEMessage,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { exponentialRetry } from '@atlassian/conversation-assistant-service/common/utils/exponential-retry';
import type { Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { StreamErrorException } from './error-classes';
import { isRetryableReconnectionError } from './reconnect-action';
import type { ChatStoreContainerProps } from './types';

const MAX_RECONNECT_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;
const POST_ONLINE_DELAY_MS = 2000;
const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

export interface ReconnectableStreamOptions<T> {
	initialGenerator: AsyncGenerator<T>;

	/**
	 * Predicate evaluated on each disconnect. Designed to close over mutable
	 * state (e.g. agentMessageId, isReconnectSupported) that is discovered
	 * during iteration, so it always reflects the latest values.
	 */
	canReconnect: () => boolean;

	/**
	 * Acquires a fresh generator from the reconnect SSE endpoint.
	 * Receives the last successfully processed event ID for resumption
	 * via the Last-Event-ID header.
	 */
	getReconnectGenerator: (lastEventId: string | undefined) => Promise<AsyncGenerator<T>>;

	/**
	 * Extracts the SSE event ID from a chunk for Last-Event-ID tracking.
	 * Return undefined for non-SSE chunks.
	 */
	getEventId: (chunk: T) => string | undefined;

	/**
	 * Called once before the first reconnect attempt.
	 * Use for one-time setup such as aligning temporary client IDs
	 * with server-assigned IDs.
	 */
	onBeforeFirstReconnect?: () => void;

	/** Called before each reconnect attempt with the current attempt number. */
	onReconnecting?: (attempt: number, maxAttempts: number) => void;

	/** Called when a new generator is successfully acquired after reconnection. */
	onReconnected?: () => void;

	/** Called on the first chunk yielded after any reconnection. */
	onFirstChunkAfterReconnect?: () => void;

	/** AbortSignal to cancel reconnection during backoff waits. */
	signal?: AbortSignal;

	maxAttempts?: number;

	/**
	 * Optional predicate checked before each retry. Return false to skip
	 * reconnection (e.g. when the chat component has unmounted and lazy
	 * reconnection should take over instead).
	 */
	shouldContinueRetrying?: () => boolean;
}

/**
 * Wraps an async generator with transparent reconnection on client-side
 * streaming errors. The consumer iterates the returned generator exactly
 * like a raw one — reconnection is an implementation detail.
 *
 * On disconnect:
 * 1. Evaluates `canReconnect()` — if false, rethrows.
 * 2. Calls `onBeforeFirstReconnect()` once for one-time ID alignment.
 * 3. Acquires a new generator via `getReconnectGenerator(lastEventId)`
 *    with exponential backoff.
 * 4. Resumes yielding chunks from the new generator.
 *
 * Server-side errors (StreamErrorException) are never retried.
 */
export async function* createReconnectableStream<T>({
	initialGenerator,
	canReconnect,
	getReconnectGenerator,
	getEventId,
	onBeforeFirstReconnect,
	onReconnecting,
	onReconnected,
	onFirstChunkAfterReconnect,
	signal,
	maxAttempts = MAX_RECONNECT_ATTEMPTS,
	shouldContinueRetrying,
}: ReconnectableStreamOptions<T>): AsyncGenerator<T> {
	let currentGenerator = initialGenerator;
	let lastEventId: string | undefined;
	let reconnectCount = 0;
	let hasCalledBeforeFirstReconnect = false;
	let awaitingFirstChunkAfterReconnect = false;

	while (true) {
		try {
			for await (const chunk of currentGenerator) {
				if (signal?.aborted) {
					return;
				}

				const eventId = getEventId(chunk);
				if (eventId) {
					lastEventId = eventId;
				}

				if (awaitingFirstChunkAfterReconnect) {
					awaitingFirstChunkAfterReconnect = false;
					onFirstChunkAfterReconnect?.();
				}

				yield chunk;
			}

			return;
		} catch (error) {
			if (error instanceof StreamErrorException) {
				throw error;
			}

			if (
				!isRetryableReconnectionError(error) ||
				!canReconnect() ||
				reconnectCount >= maxAttempts ||
				signal?.aborted
			) {
				throw error;
			}

			if (shouldContinueRetrying && !shouldContinueRetrying()) {
				throw error;
			}

			if (!hasCalledBeforeFirstReconnect) {
				onBeforeFirstReconnect?.();
				hasCalledBeforeFirstReconnect = true;
			}

			reconnectCount++;
			onReconnecting?.(reconnectCount, maxAttempts);

			currentGenerator = await exponentialRetry({
				fn: async () => getReconnectGenerator(lastEventId),
				maxRetries: 2,
				delay: RETRY_BASE_DELAY_MS,
				jitter: true,
				pauseWhileOffline: true,
				postOnlineDelay: POST_ONLINE_DELAY_MS,
				errorFilter: isRetryableReconnectionError,
				signal,
			});

			onReconnected?.();
			awaitingFirstChunkAfterReconnect = true;
		}
	}
}

// ---------------------------------------------------------------------------
// Domain-specific factory
// ---------------------------------------------------------------------------

type SSEStreamChunk = StreamMessage | ParsedSSEMessage<StreamMessage>;

export interface ChatReconnectableStreamOptions {
	generator: AsyncGenerator<SSEStreamChunk>;
	conversationId: string;
	abortController: AbortController;

	assistanceService: ChatStoreContainerProps['assistanceService'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];

	/**
	 * Getter for the server-assigned message ID. Returns undefined before
	 * the ConversationChannelDataMessage chunk is processed, which naturally
	 * prevents reconnection until the server ID is known.
	 */
	getMessageId: () => string | undefined;

	canReconnect: () => boolean;
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	getIsChatMounted?: () => boolean;

	/** One-time setup before first reconnect (e.g. ID alignment). */
	onBeforeFirstReconnect?: () => void;

	/**
	 * Additional hooks composed on top of the base behavior.
	 * Called after the built-in analytics/UI trace handling.
	 */
	onReconnecting?: (attempt: number, maxAttempts: number) => void;
	onReconnected?: () => void;
	onFirstChunkAfterReconnect?: () => void;
}

/**
 * Creates a reconnectable stream pre-wired with Rovo chat conventions:
 * - Reconnects via `assistanceService.reconnectToMessageStream`
 * - Extracts SSE event IDs from `ParsedSSEMessage` chunks
 * - Fires `rovoChatReconnection` analytics on each retry
 * - Updates conversation traces with retry status
 * - Pauses reconnection when chat is unmounted
 *
 * Both `processStream` (mid-stream) and `reconnectToInProgressStreamProvider`
 * (page-load) use this factory, passing call-site-specific hooks for
 * additional metrics/MAU/experience tracking.
 */
export function createChatReconnectableStream({
	generator,
	conversationId,
	abortController,
	assistanceService,
	sendAnalyticsEvent,
	getMessageId,
	canReconnect,
	updateCurrentConversation,
	getIsChatMounted,
	onBeforeFirstReconnect,
	onReconnecting: callerOnReconnecting,
	onReconnected: callerOnReconnected,
	onFirstChunkAfterReconnect: callerOnFirstChunkAfterReconnect,
}: ChatReconnectableStreamOptions) {
	return createReconnectableStream<SSEStreamChunk>({
		initialGenerator: generator,

		canReconnect,

		getReconnectGenerator: (lastEventId) =>
			assistanceService
				.reconnectToMessageStream({
					conversationId,
					messageId: getMessageId()!,
					lastEventId,
					controller: abortController,
				})
				.then((r: { generator: AsyncGenerator<SSEStreamChunk> }) => r.generator),

		getEventId: (chunk) => (isSSEMessage(chunk) && chunk.id ? chunk.id : undefined),

		onBeforeFirstReconnect,

		onReconnecting: (attempt, maxAttempts) => {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'retrying',
				actionSubject: 'rovoChatReconnection',
				attributes: {
					conversationId,
					messageId: getMessageId(),
					attempt,
					maxAttempts,
				},
			});
			updateCurrentConversation({
				traces: [
					{
						messageContent: `Connection lost. Retrying... (${attempt}/${maxAttempts})`,
						status: 'loading',
					},
				],
			});
			callerOnReconnecting?.(attempt, maxAttempts);
		},

		onReconnected: callerOnReconnected,
		onFirstChunkAfterReconnect: callerOnFirstChunkAfterReconnect,

		shouldContinueRetrying: getIsChatMounted
			? () => {
					if (!getIsChatMounted()) {
						abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
						return false;
					}
					return true;
				}
			: undefined,

		signal: abortController.signal,
	});
}
